import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, platformLabel } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ShoppingBag, ShieldCheck, ChevronLeft, Clock } from "lucide-react";
import { toast } from "sonner";
import Loading from "../components/Loading";
import { formatDateDisplay, getYouTubeId } from "../lib/utils";

function normaliseMarkdown(md) {
  return md
    .replace(/([^\n])\n?(#{1,3} )/g, "$1\n\n$2")
    .replace(/([^\n])(\n[ \t]*- )/g, "$1\n$2")
    .replace(/^#+\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function stripBold(text) {
  return text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1");
}

function inlineFormat(text) {
  return text
    .replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g, "<a href=\"$2\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-gold underline underline-offset-2 hover:text-gold-light\">$1</a>")
    .replace(/\*\*(.+?)\*\*/g, "<strong class=\"text-white font-bold\">$1</strong>")
    .replace(/__(.+?)__/g, "<strong class=\"text-white font-bold\">$1</strong>")
    .replace(/\*(.+?)\*/g, "<em class=\"text-slate-200 italic\">$1</em>")
    .replace(/_([^_]+)_/g, "<em class=\"text-slate-200 italic\">$1</em>");
}

function renderMarkdown(raw) {
  const md = normaliseMarkdown(raw);
  const blocks = [];
  const lines = md.split("\n");
  let listItems = [];
  let key = 0;

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <ul key={key++} className="list-none space-y-2 my-4">
        {listItems.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-slate-300 leading-relaxed text-base">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
            <span dangerouslySetInnerHTML={{ __html: inlineFormat(item) }} />
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^### (.+)/.test(line)) {
      flushList();
      blocks.push(<h3 key={key++} className="text-lg font-bold text-gold/90 mt-6 mb-2">{stripBold(line.replace(/^### /, ""))}</h3>);
    } else if (/^## (.+)/.test(line)) {
      flushList();
      blocks.push(<h2 key={key++} className="text-xl font-black tracking-tight text-white mt-8 mb-3 pb-2 border-b border-gold/20">{stripBold(line.replace(/^## /, ""))}</h2>);
    } else if (/^# (.+)/.test(line)) {
      flushList();
      blocks.push(<h2 key={key++} className="text-xl font-black tracking-tight text-white mt-8 mb-3 pb-2 border-b border-gold/20">{stripBold(line.replace(/^# /, ""))}</h2>);
    } else if (/^[-*] (.+)/.test(line)) {
      listItems.push(line.replace(/^[-*] /, ""));
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      blocks.push(<p key={key++} className="text-slate-300 leading-relaxed text-base mb-4" dangerouslySetInnerHTML={{ __html: inlineFormat(line) }} />);
    }
  }
  flushList();
  return blocks;
}

const conditionColors = { new: "bg-sky-500", used: "bg-rose-500" };

export default function ProductDetailPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const { addToCart, addToPreOrderCart, preOrderItems } = useCart();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/products/${id}`);
        setP(r.data);
        setImgIdx(0);
      } catch {
        setP(false);
      }
    })();
  }, [id]);

  if (p === false) return <div className="max-w-4xl mx-auto p-12 text-center">Product not found.</div>;
  if (!p) return <Loading variant="page" label="Loading product" />;

  const isPreOrder = p.is_preorder;
  const isDigitalCode = p.category === "digital";
  const existingPreOrder = preOrderItems.find((it) => it.product_id === p.id);
  const blockedByOther = preOrderItems.length > 0 && !existingPreOrder;

  const handleAdd = () => {
    if (!isPreOrder && p.stock <= 0) return;
    if (isPreOrder) {
      const result = addToPreOrderCart(p, qty);
      if (result === "blocked") {
        toast.error("You can't add another item to pre-order. First place the order for the existing pre-order item.");
        return;
      }
      toast.success(`${p.name} added to pre-order cart`);
    } else {
      addToCart(p, qty);
      toast.success(`${p.name} added to cart`);
    }
  };

  const productUrl = `https://gamestrade.in/product/${p.id}`;
  const productImage = p.images?.[0] || "https://gamestrade.in/og-image.jpg";
  const productDesc = p.description
    ? p.description.slice(0, 155)
    : `Buy ${p.name} at GamesTrade, Noida. Sony Authorised dealer. ₹${p.price.toLocaleString()}.`;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": p.name,
    "image": p.images || [],
    "description": p.description || productDesc,
    "brand": { "@type": "Brand", "name": p.platform || "GamesTrade" },
    "offers": {
      "@type": "Offer",
      "url": productUrl,
      "priceCurrency": "INR",
      "price": p.price,
      "availability": p.stock > 0 || p.condition === "upcoming"
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      "seller": { "@type": "Organization", "name": "GamesTrade" },
    },
  };

  return (
    <div className="site-container py-10">
      <Helmet>
        <title>{`${p.name} — GamesTrade`}</title>
        <meta name="description" content={productDesc} />
        <link rel="canonical" href={productUrl} />
        <meta property="og:title" content={`${p.name} — GamesTrade`} />
        <meta property="og:description" content={productDesc} />
        <meta property="og:url" content={productUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={productImage} />
        <meta property="product:price:amount" content={String(p.price)} />
        <meta property="product:price:currency" content="INR" />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>
      <nav className="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-6">
        <Link to="/" className="hover:text-gold transition">Home</Link>
        <ChevronLeft className="w-3 h-3 text-slate-700 rotate-180" />
        <Link to="/shop" className="hover:text-gold transition">Shop</Link>
        <ChevronLeft className="w-3 h-3 text-slate-700 rotate-180" />
        <Link to={`/shop?category=${p.category}`} className="hover:text-gold transition capitalize">{p.category}</Link>
        <ChevronLeft className="w-3 h-3 text-slate-700 rotate-180" />
        <span className="text-gold truncate max-w-[200px] normal-case tracking-normal text-xs">{p.name}</span>
      </nav>
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="relative aspect-square bg-navy-700 rounded-2xl overflow-hidden neon-border">
            {p.images?.[imgIdx] ? (
              getYouTubeId(p.images[imgIdx]) ? (
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(p.images[imgIdx])}?rel=0`}
                  title={p.name}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              ) : (
                <img src={p.images[imgIdx]} alt={p.name} className="w-full h-full object-contain p-6" />
              )
            ) : null}
            <span className={`absolute top-4 left-4 ${isPreOrder ? "bg-gold text-navy-900" : conditionColors[p.condition] + " text-white"} text-[10px] font-black uppercase tracking-[0.15em] px-3 py-1.5 rounded`}>
              {isPreOrder ? "PRE-ORDER" : p.condition === "new" ? "NEW" : "USED"}
            </span>
            {p.sony_authorised && (
              <span className="absolute top-4 right-4 bg-navy-900/80 backdrop-blur text-gold text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Sony Authorised
              </span>
            )}
          </div>
          {p.images && p.images.length > 1 && (
            <div className="flex gap-2 mt-3">
              {p.images.map((im, i) => {
                const ytId = getYouTubeId(im);
                return (
                  <button key={i} onClick={() => setImgIdx(i)} className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition ${i === imgIdx ? "border-gold" : "border-transparent opacity-60"}`}>
                    {ytId ? (
                      <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt="video" className="w-full h-full object-cover" />
                    ) : (
                      <img src={im} alt="" className="w-full h-full object-contain p-1" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold/80 mb-2">{platformLabel(p.platform)} • {p.category}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase leading-tight">{p.name}</h1>
          <div className="flex items-baseline gap-3 mt-6">
            <span className="text-4xl font-black text-gold">₹{p.price.toLocaleString()}</span>
            {p.original_price && p.original_price > p.price && (
              <>
                <span className="text-lg text-slate-500 line-through">₹{p.original_price.toLocaleString()}</span>
                <span className="text-sm font-bold text-emerald-400">{Math.round((1 - p.price / p.original_price) * 100)}% OFF</span>
              </>
            )}
          </div>

          {isPreOrder && (
            <div className="mt-4 bg-gold/10 border border-gold/40 rounded-xl px-5 py-4 flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gold shrink-0" />
                <span className="font-black text-gold uppercase tracking-widest text-xs">Pre-Order — Not Available Yet</span>
              </div>
              {p.release_date ? (
                <div className="text-slate-200 text-sm font-semibold mt-1">
                  Releasing on <span className="text-gold">{formatDateDisplay(p.release_date)}</span>
                </div>
              ) : null}
              <div className="text-xs text-slate-400 mt-0.5">Pre-book now to secure your copy. Full payment is collected at the time of booking.</div>
            </div>
          )}

          {isDigitalCode && (
            <div className="mt-4 bg-sky-500/10 border border-sky-500/30 rounded-lg px-4 py-3 text-sm text-slate-300">
              <span className="font-bold text-sky-300 uppercase tracking-wider">Digital code delivery</span>
              <span> · No shipping charge applies. A 2.5% processing fee is added at checkout for digital codes.</span>
            </div>
          )}

          {blockedByOther && isPreOrder && (
            <div className="mt-3 bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-3 text-xs text-rose-400 font-semibold">
              Your pre-order cart already has a different product. Only 1 unique pre-order is allowed at a time.
            </div>
          )}

          {existingPreOrder && (
            <div className="mt-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-3 text-xs text-emerald-400 font-semibold">
              Already in your pre-order cart (qty: {existingPreOrder.quantity}). Adding more will increase the quantity.
            </div>
          )}

          <div className="mt-8 flex items-center gap-3">
            <div className="inline-flex items-center bg-navy-700 rounded-full overflow-hidden">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 hover:text-gold" data-testid="qty-decrease">−</button>
              <span className="px-4 font-bold" data-testid="qty-display">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-3 hover:text-gold" data-testid="qty-increase">+</button>
            </div>
            <button
              onClick={handleAdd}
              disabled={(isPreOrder && blockedByOther) || (!isPreOrder && p.stock <= 0)}
              data-testid="add-to-cart-button"
              className="flex-1 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-7 py-3.5 rounded-full hover:bg-gold-light btn-glow transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <ShoppingBag className="w-4 h-4" />
              {isPreOrder
                ? (blockedByOther ? "Pre-Order Cart Full" : "Pre-Book Now")
                : (p.stock > 0 ? "Add to Cart" : "Out of Stock")}
            </button>
          </div>

        </div>
      </div>

      {p.description && (
        <div className="mt-10 border-t border-gold/20 pt-8">
          <h3 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold mb-4">Description</h3>
          <div>{renderMarkdown(p.description)}</div>
        </div>
      )}

      {p.specs && Object.keys(p.specs).length > 0 && (
        <div className="mt-10 border-t border-gold/20 pt-8">
          <h3 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold mb-3">Specs</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {Object.entries(p.specs).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-gold/10 py-1.5">
                <dt className="text-slate-400 capitalize">{k}</dt>
                <dd className="font-semibold">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
